import React from 'react';
import AuthApi, { AbilityAuthorization, SsoConfig } from '../api/AuthApi';
import { handleUnauthorizedResponse } from '../api/http';
import { Role } from '../components/admin/tenant/api/roles';
import { dateConversion } from '../components/fastForm/utils';
import LargeCenterSpinner from '../components/LargeCenterSpinner';
import { Permission } from '../permissions';
import hasPermissionForAuth from '../util/hasPermission';
import { formatUNIXTimestamp } from '../util/util';

const authKey = 'auth';
const authAdminKey = 'authAdmin';
const reauthenticateTimeoutBufferMs = 10 * 60 * 1000; // 10 minutes

export const ROLES_AND_PERMISSION__ACCOUNT_TYPE = 'account.useRoles';

// hide inactive entities
export const UI_HIDE_INACTIVE_ENTITIES_ENABLED =
	'property.ui.hide.inactive.entities.enabled';

// Cash Management Feature Flag
export const UI_CASH_MANAGEMENT_FEATURE_ENABLED =
	'property.cash.management.enabled';

// The normal account type that can perform write operations on policies, claims, etc
export const DEPRECATED__EMPLOYEE__ACCOUNT_TYPE = 'account.tenant.employee';

// Cannot perform any type of write operation. Only has read access.
export const DEPRECATED__READ_ONLY_USER__ACCOUNT_TYPE =
	'account.tenant.read.only.user';

// Can only modify claims. Everything else is read-only access. Cannot modify payables but can update reserves.
export const DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE =
	'account.tenant.claims.only.user';

// A tenant admin can create/modify users
export const DEPRECATED__TENANT_ADMIN_ACCOUNT_TYPE = 'account.tenant.admin';

// An internal admin. The differentiator is that this account can create tenant admin accounts?
export const INTERNAL_ADMIN__ACCOUNT_TYPE = 'account.internal';

// Multi currency feature flag:
export const MULTI_CURRENCY_FEATURE_ENABLE = 'property.multicurrency.enabled';

// Multi currency UI feature flag:
export const UI_MULTI_CURRENCY_FEATURE_ENABLE =
	'property.ui.multicurrency.enabled';

// Data autofill feature flag:
export const DATA_AUTOFILL_FEATURE_ENABLE = 'property.autofill.enabled';

// Hide peril coverage limits feature flag:
export const HIDE_PERIL_LIMITS_ENABLE = 'property.ui.hide.peril.limits.enabled';

// Show second precision on endorsement dates feature flag:
export const SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED =
	'property.ui.endorsement.precision.second.enabled';

// Roles and Permissions enabled
export const ROLES_AND_PERMISSION_ENABLED =
	'property.roles.and.permissions.enabled';

export const TABLES_AND_STATIC_DOCS_ENABLED =
	'property.ui.tables.and.staticdocs.enabled';

export const ENDORSEMENT_POLICY_END_DATE_ENABLED =
	'property.endorsement.update.policydate.enabled';

// Core search enabled
export const CORE_SEARCH_ENABLED = 'property.search.service.enabled';

export const AuthContext = React.createContext<AuthData>(null);

export const useAuthContext = () => React.useContext(AuthContext);

export function withAuthContext<T>(Component: any) {
	return (props: Omit<T, 'authData'>) => (
		<AuthContext.Consumer>
			{(authData) => <Component {...props} authData={authData} />}
		</AuthContext.Consumer>
	);
}

export interface AuthInfo {
	accountId: string;
	accountType: string;
	authorizationToken: string;
	authorizationExpiresTimestamp: string; // ISO-8601
	rememberMe: boolean;
	rememberMeExpiration: string; // ISO-8601
	displayName: string;
	tenantId: string;
	tenantName: string;
	tenantType: string;
	ontologyId: string;
	abilityAuthorizations: AbilityAuthorization[];
	timezone: string;
	configProperties: { [property: string]: boolean };
	authenticated: boolean;
	username: string;
	hostName: string;
}

export interface Tenant {
	hostname: string;
	locator: string;
}

export interface TenantAuthorizationData {
	accountId: string;
	displayName: string;
	accountType: string;
	tenantId: string;
	tenantName: string;
	tenantType: string;
	authorizationIssueTimestamp: string;
	authorizationExpiresTimestamp: string;
	ontologyId: string;
	timezone: string;
	tenantAssetTimestamp: string;
	roles: string[];
	abilityAuthorizations: any[];
	configProperties: { [property: string]: boolean };
	authorizationToken: string;
}

export interface AuthAdminResponse {
	abilityAuthorizations: any[];
	accountId: string;
	accountType: string;
	authorizationExpiresTimestamp: string;
	authorizationIssueTimestamp: string;
	authorizationToken: string;
	configProperties: {};
	displayName: string;
	roles: any[];
}

export type AuthStatus = 'EXPIRED' | 'INVALID' | 'VALID';

export interface AuthData {
	auth: AuthInfo;
	adminAuthData: AuthAdminResponse;
	tenantAuthorizationData: TenantAuthorizationData;
	isRenewing: boolean; // used to prevent infinite loop of renewing auth
	ssoEnabled: boolean;
	reauthenticateTimeoutId: any;
	loading: boolean;
	ssoConfig: SsoConfig;
	permissions: Permission[];
	isLoggedOut: boolean;
	hasPermission: (
		permission: Permission,
		rolesAndPermissionEnabledOverload?: boolean,
	) => boolean;
	clear: () => void;
	logout: () => void;
	getAuthStatus: () => AuthStatus;
	deprecated_getAuthPermissions: () => 'ALL' | 'READ_ONLY' | 'CLAIMS_ONLY';
	getFormattedDate: (timestamp: string) => string;
	getZonedFormattedDateString: (timestamp: string, format?: string) => string;
	isValid: () => boolean;
	clearAuthIfExpired: () => void;
	setAuth: (
		authResp: AuthInfo,
		username: string,
		hostname: string,
	) => Promise<AuthData>;
	setIsLoggedOut: (isLoggedOut: boolean) => Promise<AuthData>;
	setAdminAuth: (authResp: AuthAdminResponse) => Promise<AuthData>;
	setTenantAuth: (authResp: TenantAuthorizationData) => Promise<AuthData>;
	getLoggedInAccount: () => void;
	setLoading: (loading: boolean) => Promise<AuthData>;
	setSsoEnabled: (ssoEnabled: boolean) => Promise<AuthData>;
	setSsoConfig: (ssoConfig: SsoConfig) => Promise<AuthData>;
	setPermissions: (roles: Role[]) => Promise<AuthData>;
}

export class AuthProvider extends React.Component<{}, AuthData> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			// Will attempt to set this later in the ctor
			auth: null,
			adminAuthData: null,
			loading: true,
			isRenewing: false,
			reauthenticateTimeoutId: null,
			tenantAuthorizationData: null,
			ssoEnabled: false,
			isLoggedOut: false,
			ssoConfig: null,
			permissions: [],
			hasPermission: this.hasPermission,
			clear: this.clear,
			getAuthStatus: this.getAuthStatus,
			deprecated_getAuthPermissions: this.deprecated_getAuthPermissions,
			getFormattedDate: this.getFormattedDate,
			getZonedFormattedDateString: this.getZonedFormattedDateString,
			isValid: this.isValid,
			clearAuthIfExpired: this.clearAuthIfExpired,
			setAuth: this.setAuth,
			setAdminAuth: this.setAdminAuth,
			setTenantAuth: this.setTenantAuth,
			setLoading: this.setLoading,
			getLoggedInAccount: this.getLoggedInAccount,
			setSsoEnabled: this.setSsoEnabled,
			setSsoConfig: this.setSsoConfig,
			setPermissions: this.setPermissions,
			setIsLoggedOut: this.setIsLoggedOut,
			logout: this.logout,
		};
	}

	public async componentDidMount() {
		let ssoConfig: SsoConfig = null;
		if (!this.state.ssoConfig) {
			ssoConfig = await AuthApi.getSsoFlag();
			await this.setSsoEnabled(ssoConfig.ssoEnabled);
			await this.setSsoConfig(ssoConfig);
		}
		//add reload admin
		if (!ssoConfig.ssoEnabled) {
			const storage = window.localStorage;
			const auth: AuthInfo = JSON.parse(storage.getItem(authKey));
			const adminAuth: AuthAdminResponse = JSON.parse(
				storage.getItem(authAdminKey),
			);
			if (auth) {
				// Reauthenticate from local storage on mount
				this.setAuth(auth, auth.username, auth.hostName);
				const account = await AuthApi.getAccount(
					auth.accountId,
					auth.authorizationToken,
				).catch((error) => {
					handleUnauthorizedResponse(error, this.state);
				});
				this.setPermissions(account && account.roles);
			} else if (adminAuth) {
				this.setAdminAuth(adminAuth);
			}
		}
		await this.setState({ loading: false });
	}

	public render() {
		if (this.state.loading) {
			return <LargeCenterSpinner />;
		}

		return (
			<AuthContext.Provider value={this.state}>
				{this.props.children}
			</AuthContext.Provider>
		);
	}

	private logout = async () => {
		const { ssoEnabled, ssoConfig } = this.state;
		if (ssoEnabled && ssoConfig.ssoVersion === 'v1') {
			window.location.assign('/signout');
		} else if (ssoEnabled && ssoConfig.ssoVersion === 'v0') {
			await AuthApi.getOauthLogout();
			window.location.reload();
		} else {
			this.clear();
		}
	};

	private clear = async () => {
		clearTimeout(this.state.reauthenticateTimeoutId);
		window.localStorage.removeItem(authKey);
		window.localStorage.removeItem(authAdminKey);
		await this.setState({
			auth: null,
			tenantAuthorizationData: null,
			adminAuthData: null,
			reauthenticateTimeoutId: null,
		});
	};

	private setAuth = (
		authResp: AuthInfo,
		username: string,
		hostName: string,
	) => {
		const userAuthObj = { ...authResp, username, hostName };
		const promise = new Promise<AuthData>((resolve, reject) => {
			const userAuthObjForStorage = JSON.stringify(userAuthObj);
			// store in local storage for now
			// TODO: add reauthenticate function that will ping the api to check if authentication still valid
			// and clear auth from localstorage if not valid
			window.localStorage.setItem(authKey, userAuthObjForStorage);

			this.setState({ auth: userAuthObj }, () => {
				this.createReauthenticateTimeout();
				resolve(this.state);
			});
		});
		return promise;
	};

	private setAdminAuth = (authResp: AuthAdminResponse) => {
		const promise = new Promise<AuthData>((resolve, reject) => {
			const userAuthObjForStorage = JSON.stringify(authResp);
			// store in local storage for now
			// TODO: add reauthenticate function that will ping the api to check if authentication still valid
			// and clear auth from localstorage if not valid
			window.localStorage.setItem(authAdminKey, userAuthObjForStorage);

			this.setState({ adminAuthData: authResp }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private getAuthStatus = () => {
		// If there's no auth token,
		const { auth } = this.state;

		if (!auth) {
			return 'INVALID';
		}

		// Check if auth token has expired.
		const { authorizationToken, authorizationExpiresTimestamp } = auth;
		const now = Date.now();
		const expTimestamp = Number.parseInt(authorizationExpiresTimestamp, 10);

		if (authorizationToken && expTimestamp) {
			if (expTimestamp > now) {
				// It's valid
				return 'VALID';
			} else {
				return 'EXPIRED';
			}
		}
		return 'INVALID';
	};

	private deprecated_getAuthPermissions = () => {
		const { auth } = this.state;
		const { accountType } = auth;
		if (
			accountType === INTERNAL_ADMIN__ACCOUNT_TYPE ||
			accountType === DEPRECATED__TENANT_ADMIN_ACCOUNT_TYPE ||
			accountType === DEPRECATED__EMPLOYEE__ACCOUNT_TYPE
		) {
			return 'ALL';
		} else if (accountType === DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE) {
			return 'CLAIMS_ONLY';
		}
		return 'READ_ONLY';
	};

	private getFormattedDate = (timestamp: string) => {
		const { auth } = this.state;
		const { timezone } = auth;
		return formatUNIXTimestamp(timestamp, timezone);
	};

	private getZonedFormattedDateString = (
		timestamp: string,
		format?: string,
	) => {
		const { auth } = this.state;
		const { timezone } = auth;
		const dateObject = dateConversion.convertTimestampToDayJS(
			timestamp,
			timezone,
		);
		return dateObject.format(format || 'YYYY/MM/DD');
	};

	private isValid = () => {
		return this.getAuthStatus() === 'VALID';
	};

	private createReauthenticateTimeout = () => {
		clearTimeout(this.state.reauthenticateTimeoutId);
		const { auth, ssoEnabled } = this.state;
		const authorizationExpirationDate = new Date(
			parseInt(auth.authorizationExpiresTimestamp, 10),
		);
		if (isNaN(authorizationExpirationDate.getTime())) {
			return;
		}

		const cacheDuration = authorizationExpirationDate.getTime() - Date.now();
		const { authorizationToken } = this.state.auth;
		const reauthenticateTimeoutMs =
			cacheDuration - reauthenticateTimeoutBufferMs;

		const reauthenticateTimeoutId = setTimeout(() => {
			if (ssoEnabled) {
				this.getLoggedInAccount();
			} else {
				AuthApi.reAuthenticateInternal(authorizationToken)
					.then((authResponse: any) => {
						this.setAuth(authResponse.data, auth.username, auth.hostName);
					})
					.catch((error: any) => {
						console.error(error);
					});
			}
		}, reauthenticateTimeoutMs);

		this.setState({ reauthenticateTimeoutId });
	};

	private clearAuthIfExpired = () => {
		if (this.getAuthStatus() === 'EXPIRED') {
			this.clear();
		}
	};

	private getLoggedInAccount = async () => {
		if (this.state.ssoEnabled) {
			try {
				const authResponse = await AuthApi.getLoggedInAccount();
				this.setAuth(
					authResponse,
					authResponse.username,
					authResponse.hostName,
				);
				const account = await AuthApi.getAccount(
					authResponse.accountId,
					authResponse.authorizationToken,
				);

				await this.setPermissions(account.roles);
			} catch (error) {
				console.error(error);
			}
		}
	};

	private setSsoConfig = (ssoConfig: SsoConfig) => {
		const promise = new Promise<AuthData>((resolve, reject) => {
			this.setState({ ssoConfig }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setSsoEnabled = (ssoEnabled: boolean) => {
		const promise = new Promise<AuthData>((resolve) => {
			this.setState({ ssoEnabled }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setLoading = (loading: boolean) => {
		const promise = new Promise<AuthData>((resolve) => {
			this.setState({ loading }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPermissions = (roles: Role[]) => {
		const promise = new Promise<AuthData>((resolve) => {
			let permissions = [] as Permission[];
			roles.forEach((role) => {
				role.permissions.forEach((r_permission) => {
					permissions.push(r_permission);
				});
			});
			this.setState({ permissions }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	/**
	 * Takes a permission (e.g. 'policy.fetch') and returns true if the user has that permission
	 * If the user has a * permission for the permissionScope, it will return true (ex: 'policy.fetch' will return true if the user has 'policy.*')
	 * @private
	 * @param {Permission} permissionName
	 * @memberof AuthProvider
	 */
	private hasPermission = (
		permissionName: Permission,
		rolesAndPermissionEnabledOverload?: boolean,
	) => {
		return hasPermissionForAuth(
			this.state.permissions,
			permissionName,
			rolesAndPermissionEnabledOverload
				? rolesAndPermissionEnabledOverload
				: this.state.auth?.configProperties?.[ROLES_AND_PERMISSION_ENABLED],
			this.state.auth?.accountType,
		);
	};

	private setTenantAuth = (
		tenantAuthorizationData: TenantAuthorizationData,
	) => {
		const promise = new Promise<AuthData>((resolve) => {
			this.setState({ tenantAuthorizationData }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setIsLoggedOut = (isLoggedOut: boolean) => {
		const promise = new Promise<AuthData>((resolve) => {
			this.setState({ isLoggedOut }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};
}
